import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { checkCampaign } from '../../services/api';

export default function ProblemsPanel({ campaignId, lastProblems, resetKey }) {
  const [checking, setChecking] = useState(false);
  const [problems, setProblems] = useState(null); // null = not checked this session

  // A result is only valid for the members/default it was checked against.
  // The stored lastProblems are stale too once members/default change, so hide them until the next Check.
  const [stale, setStale] = useState(false);
  const firstKey = useRef(resetKey);
  useEffect(() => {
    if (resetKey === firstKey.current) return;
    setProblems(null);
    setStale(true);
  }, [resetKey]);

  const run = async () => {
    setChecking(true);
    try {
      const data = await checkCampaign(campaignId);
      setProblems(data.problems || []);
      setStale(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  const shown = problems ?? (!stale && lastProblems && lastProblems.length ? lastProblems : null);

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4" data-testid="problems-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">Pre-launch check</h2>
          <p className="text-xs text-[#A1A1AA] mt-0.5">Looks for problems that would stop or break a launch.</p>
        </div>
        <Button variant="outline" size="sm" className="border-[#27272A]" onClick={run} disabled={checking} data-testid="check-button">
          {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          Check
        </Button>
      </div>
      {problems && problems.length === 0 && (
        <p className="mt-3 flex items-center gap-2 text-sm text-[#06C755]" data-testid="check-ready">
          <CheckCircle2 className="w-4 h-4" /> Ready to launch
        </p>
      )}
      {shown && shown.length > 0 && (
        <ul className="mt-3 space-y-2">
          {shown.map((p, i) => (
            <li
              key={`${p.code}-${p.menuId || ''}-${i}`}
              className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2"
              data-testid="problem-item"
            >
              <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
              <span>{p.menuName && <strong className="font-semibold">{p.menuName}: </strong>}{p.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
